import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserService } from './user.service';

interface ReportField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'array';
  source: string;
  path: string;
  category: string;
  description?: string;
  accessLevel: string[];
}

interface QueryCondition {
  field: string;
  operator: string;
  value: any;
  secondValue?: any;
}

interface CustomReport {
  _id?: string;
  name: string;
  description?: string;
  selectedFields: string[];
  conditions: QueryCondition[];
  orderBy?: { field: string; direction: 'asc' | 'desc' }[];
  limit?: number;
  isPublic: boolean;
  exportFormats: string[];
  createdBy?: any;
  subscriberId?: string;
  lastExecuted?: Date;
  executionCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

interface ExecutionResult {
  data: any[];
  total: number;
  executionTime: number;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CustomReportService {
  private readonly apiUrl = `${environment.apiUrl}/custom-reports`;
  private readonly simpleReportsApiUrl = `${environment.apiUrl}/simple-reports`;

  constructor(private http: HttpClient, private userService: UserService) {}

  /**
   * Get available fields for report building
   */
  async getAvailableFields(): Promise<{ fields: ReportField[]; fieldsByCategory: { [category: string]: ReportField[] } }> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ fields: ReportField[]; fieldsByCategory: { [category: string]: ReportField[] } }>>(
        `${this.apiUrl}/fields`
      )
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load available fields');
    }
    
    return response.data;
  }

  /**
   * Get reports for current user
   */
  async getUserReports(): Promise<CustomReport[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<CustomReport[]>>(this.apiUrl)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load reports');
    }
    
    return response.data;
  }

  /**
   * Get a specific report by ID
   */
  async getReportById(reportId: string): Promise<CustomReport> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<CustomReport>>(`${this.apiUrl}/${reportId}`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load report');
    }
    
    return response.data;
  }

  /**
   * Create a new custom report
   */
  async createReport(reportData: Partial<CustomReport>): Promise<CustomReport> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<CustomReport>>(this.apiUrl, reportData)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to create report');
    }
    
    return response.data;
  }

  /**
   * Update an existing report
   */
  async updateReport(reportId: string, reportData: Partial<CustomReport>): Promise<CustomReport> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<CustomReport>>(`${this.apiUrl}/${reportId}`, reportData)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update report');
    }
    
    return response.data;
  }

  /**
   * Delete a report
   */
  async deleteReport(reportId: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<any>>(`${this.apiUrl}/${reportId}`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete report');
    }
  }

  /**
   * Preview report data (limited results for testing)
   */
  async previewReport(reportData: Partial<CustomReport>): Promise<any[]> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<any[]> & { total?: number; executionTime?: number; message?: string }>(
        `${this.apiUrl}/preview`, 
        reportData
      )
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to preview report');
    }
    
    // Handle both nested and flat response structures
    return Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
  }

  /**
   * Execute a report and return data with pagination
   */
  async executeReport(
    reportId: string, 
    page: number = 1, 
    limit: number = 100
  ): Promise<ExecutionResult> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    const response = await firstValueFrom(
      this.http.post<ApiResponse<any[]> & { pagination?: { total: number; page: number; limit: number; pages: number }; executionTime?: number }>(
        `${this.apiUrl}/${reportId}/execute`, 
        {},
        { params }
      )
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to execute report');
    }
    
    return {
      data: response.data || [],
      total: response.pagination?.total || response.data?.length || 0,
      executionTime: (response as any).executionTime || 0
    };
  }

  /**
   * Export report to CSV
   */
  async exportReport(reportId: string, format: 'csv' | 'excel' | 'pdf'): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.apiUrl}/${reportId}/export/${format}`, {
          responseType: 'blob',
          observe: 'response'
        })
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `report.${format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const blob = response.body;
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      if (error.error instanceof Blob) {
        // Parse error from blob response
        const text = await error.error.text();
        try {
          const errorObj = JSON.parse(text);
          throw new Error(errorObj.message || 'Export failed');
        } catch {
          throw new Error('Export failed');
        }
      } else {
        throw new Error(error.message || 'Export failed');
      }
    }
  }

  /**
   * Get report execution statistics
   */
  async getReportStats(reportId: string): Promise<any> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<any>>(`${this.apiUrl}/${reportId}/stats`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load report statistics');
    }
    
    return response.data;
  }

  // Observable versions for reactive programming (optional)
  
  getAvailableFields$(): Observable<ApiResponse<{ fields: ReportField[]; fieldsByCategory: { [category: string]: ReportField[] } }>> {
    return this.http.get<ApiResponse<{ fields: ReportField[]; fieldsByCategory: { [category: string]: ReportField[] } }>>(
      `${this.apiUrl}/fields`
    );
  }

  getUserReports$(): Observable<ApiResponse<CustomReport[]>> {
    return this.http.get<ApiResponse<CustomReport[]>>(this.apiUrl);
  }

  getReportById$(reportId: string): Observable<ApiResponse<CustomReport>> {
    return this.http.get<ApiResponse<CustomReport>>(`${this.apiUrl}/${reportId}`);
  }

  createReport$(reportData: Partial<CustomReport>): Observable<ApiResponse<CustomReport>> {
    return this.http.post<ApiResponse<CustomReport>>(this.apiUrl, reportData);
  }

  updateReport$(reportId: string, reportData: Partial<CustomReport>): Observable<ApiResponse<CustomReport>> {
    return this.http.put<ApiResponse<CustomReport>>(`${this.apiUrl}/${reportId}`, reportData);
  }

  deleteReport$(reportId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/${reportId}`);
  }

  previewReport$(reportData: Partial<CustomReport>): Observable<ApiResponse<{ data: any[]; total: number; executionTime: number }>> {
    return this.http.post<ApiResponse<{ data: any[]; total: number; executionTime: number }>>(
      `${this.apiUrl}/preview`, 
      reportData
    );
  }

  executeReport$(reportId: string, page: number = 1, limit: number = 100): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.post(`${this.apiUrl}/${reportId}/execute`, {}, { params });
  }

  getReportStats$(reportId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${reportId}/stats`);
  }

  // Simple Reports Methods
  
  /**
   * Execute a simple report with basic configuration
   */
  async executeSimpleReport(reportConfig: {
    name: string;
    description?: string;
    dataSource: string;
    fields: string[];
    filters: any[];
    dateRangeFilter?: {
      field: string;
      startDate: Date | null;
      endDate: Date | null;
    } | null;
    chartConfig?: any;
    page?: number;
    limit?: number;
  }): Promise<ExecutionResult> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<any[]> & { pagination?: { total: number; page: number; limit: number; pages: number }; executionTime?: number }>(
        `${this.simpleReportsApiUrl}/simple-execute`, 
        reportConfig
      )
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to execute simple report');
    }
    
    return {
      data: response.data || [],
      total: response.pagination?.total || response.data?.length || 0,
      executionTime: (response as any).executionTime || 0
    };
  }

  /**
   * Export simple report
   */
  async exportSimpleReport(reportId: string, format: 'csv' | 'excel' | 'pdf'): Promise<void> {
    try {
      console.log(`Starting export for report ${reportId} in ${format} format`);
      
      const response = await firstValueFrom(
        this.http.get(`${this.simpleReportsApiUrl}/simple-export/${reportId}/${format}`, {
          responseType: 'blob',
          observe: 'response'
        })
      );

      console.log('Export response received:', response.status, response.statusText);

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `simple-report.${format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      console.log('Downloading file:', filename);

      // Create download link
      const blob = response.body;
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('Download initiated successfully');
      } else {
        console.error('Empty or invalid blob received');
        throw new Error('No data received from server');
      }
    } catch (error: any) {
      console.error('Export error details:', error);
      
      if (error.error instanceof Blob) {
        // Parse error from blob response
        try {
          const text = await error.error.text();
          const errorObj = JSON.parse(text);
          throw new Error(errorObj.message || 'Export failed');
        } catch (parseError) {
          throw new Error('Export failed - invalid response format');
        }
      } else if (error.status) {
        throw new Error(`Export failed with status ${error.status}: ${error.message}`);
      } else {
        throw new Error(error.message || 'Export failed');
      }
    }
  }

  /**
   * Export simple report directly from configuration (without saving)
   */
  async exportSimpleReportDirect(reportConfig: {
    name: string;
    description?: string;
    dataSource: string;
    fields: string[];
    filters: any[];
    chartConfig?: any;
  }, format: 'csv' | 'excel' | 'pdf'): Promise<void> {
    try {
      console.log('Starting direct export with config:', reportConfig);
      
      // Execute the report to get the data
      const result = await this.executeSimpleReport(reportConfig);
      
      if (!result.data || result.data.length === 0) {
        throw new Error('No data available to export');
      }
      
      console.log(`Exporting ${result.data.length} records in ${format} format`);
      
      // Export the data directly using client-side processing
      await this.exportDataDirectly(result.data, reportConfig.fields, reportConfig.name, format);
      
    } catch (error: any) {
      console.error('Direct export error:', error);
      
      // Fallback: try the backend export method
      console.log('Falling back to backend export method...');
      try {
        await this.exportSimpleReportBackend(reportConfig, format);
      } catch (fallbackError: any) {
        console.error('Backend export also failed:', fallbackError);
        throw new Error(`Export failed: ${error.message}. Fallback also failed: ${fallbackError.message}`);
      }
    }
  }

  /**
   * Fallback method using backend export
   */
  private async exportSimpleReportBackend(reportConfig: {
    name: string;
    description?: string;
    dataSource: string;
    fields: string[];
    filters: any[];
    chartConfig?: any;
  }, format: 'csv' | 'excel' | 'pdf'): Promise<void> {
    let tempReportId: string | null = null;
    
    try {
      // Create a temporary report
      const tempReport = await this.saveSimpleReport({
        name: `Temp Export - ${Date.now()}`,
        description: 'Temporary report for backend export',
        dataSource: reportConfig.dataSource,
        fields: reportConfig.fields,
        filters: reportConfig.filters,
        chartConfig: reportConfig.chartConfig,
        isPublic: false
      });

      tempReportId = tempReport._id;

      // Export the temporary report
      if (tempReportId) {
        await this.exportSimpleReport(tempReportId, format);
      } else {
        throw new Error('Failed to create temporary report');
      }
      
    } finally {
      // Clean up the temporary report after a delay
      if (tempReportId) {
        setTimeout(async () => {
          try {
            await this.deleteSavedSimpleReport(tempReportId!);
            console.log('Temporary report cleaned up successfully');
          } catch (cleanupError) {
            console.warn('Failed to cleanup temp report:', cleanupError);
          }
        }, 3000); // Wait 3 seconds before cleanup
      }
    }
  }

  /**
   * Export data directly without backend involvement
   */
  private async exportDataDirectly(data: any[], fields: string[], reportName: string, format: 'csv' | 'excel' | 'pdf'): Promise<void> {
    const timestamp = new Date().toISOString().split('T')[0];
    const cleanReportName = reportName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    // Map format to correct file extension
    const fileExtension = format === 'excel' ? 'xlsx' : format;
    const filename = `${cleanReportName}_${timestamp}.${fileExtension}`;
    
    switch (format) {
      case 'csv':
        await this.exportToCSVClientSide(data, fields, filename);
        break;
      case 'excel':
        await this.exportToExcelClientSide(data, fields, filename);
        break;
      case 'pdf':
        await this.exportToPDFClientSide(data, fields, filename, reportName);
        break;
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  }

  /**
   * Export to CSV using client-side processing
   */
  private async exportToCSVClientSide(data: any[], fields: string[], filename: string): Promise<void> {
    // Create CSV content
    const headers = fields.join(',');
    const rows = data.map(row => 
      fields.map(field => {
        const value = row[field];
        // Escape CSV values
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );
    
    const csvContent = [headers, ...rows].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, filename);
  }

  /**
   * Export to Excel using client-side processing
   */
  private async exportToExcelClientSide(data: any[], fields: string[], filename: string): Promise<void> {
    // Import XLSX dynamically
    const XLSX = await import('xlsx');
    
    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(data.map(row => {
      const obj: any = {};
      fields.forEach(field => {
        obj[field] = row[field];
      });
      return obj;
    }));
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    this.downloadBlob(blob, filename);
  }

  /**
   * Export to PDF using client-side processing
   */
  private async exportToPDFClientSide(data: any[], fields: string[], filename: string, reportName?: string): Promise<void> {
    // Check if there are too many fields for PDF display
    const maxFieldsForPDF = 8; // Maximum fields that can be displayed properly in landscape PDF
    const warningFieldsThreshold = 10; // Threshold for showing warning about display issues
    
    if (fields.length > maxFieldsForPDF) {
      throw new Error(`Too many fields (${fields.length}) for PDF export. Maximum ${maxFieldsForPDF} fields supported. Please use CSV or Excel export instead.`);
    }
    
    // Show warning if there are more than 10 fields but still allow export
    if (fields.length > warningFieldsThreshold) {
      console.warn(`PDF export with ${fields.length} fields may not display correctly due to space constraints.`);
      // Continue with export but with warning
    }
    
    // Import jsPDF dynamically
    const { jsPDF } = await import('jspdf');
    
    // Create PDF in landscape orientation
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Get current user info (you may need to inject a user service)
    const currentUser = this.getCurrentUser();
    
    // Set up the document header
    doc.setFontSize(18);
    doc.text('Report Data', 14, 15);
    
    // Add report metadata
    doc.setFontSize(10);
    const displayReportName = reportName || filename.replace(/\.pdf$/, '').replace(/_/g, ' ');
    doc.text(`Report: ${displayReportName}`, 14, 25);
    
    if (currentUser) {
      doc.text(`Generated by: ${currentUser.firstName} ${currentUser.lastName}`, 14, 30);
    }
    
    doc.text(`Generated on: ${this.formatDateForPDF(new Date())}`, 14, 35);
    
    // Add table headers
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const colWidth = (pageWidth - (margin * 2)) / fields.length;
    let yPosition = 45; // Moved down to accommodate header info
    
    // Draw table headers with background
    doc.setFontSize(10);
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPosition - 5, pageWidth - (margin * 2), 8, 'F');
    
    fields.forEach((field, index) => {
      const xPosition = margin + (index * colWidth);
      doc.text(field, xPosition, yPosition);
    });
    
    // Draw line under headers
    yPosition += 5;
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 5;
    
    // Add data rows
    data.slice(0, 50).forEach((row, rowIndex) => { // Limit to 50 rows for PDF
      // Check if we need a new page
      if (yPosition > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPosition = 20;
        
        // Redraw headers on new page
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPosition - 5, pageWidth - (margin * 2), 8, 'F');
        fields.forEach((field, index) => {
          const xPosition = margin + (index * colWidth);
          doc.text(field, xPosition, yPosition);
        });
        yPosition += 5;
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 5;
      }
      
      fields.forEach((field, colIndex) => {
        const value = row[field] || '';
        const xPosition = margin + (colIndex * colWidth);
        const formattedValue = this.formatValueForPDF(value, field);
        const text = String(formattedValue).substring(0, 25); // Increased truncation for landscape
        doc.text(text, xPosition, yPosition);
      });
      
      yPosition += 7;
    });
    
    // Add footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, doc.internal.pageSize.getHeight() - 10);
    }
    
    // Save the PDF
    doc.save(filename);
    console.log(`PDF exported: ${filename}`);
  }

  /**
   * Format date for PDF display
   */
  private formatDateForPDF(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Format value for PDF display based on field type
   */
  private formatValueForPDF(value: any, fieldName: string): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    // Handle date fields
    if (fieldName.toLowerCase().includes('date') || fieldName.toLowerCase().includes('time')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
      } catch (error) {
        // If date parsing fails, return original value
      }
    }

    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle numeric values
    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    // Return string value as is
    return String(value);
  }

  /**
   * Get current user info for PDF header
   */
  private getCurrentUser(): any {
    try {
      // Try multiple storage keys as fallback
      const possibleKeys = ['currentUser', 'user', 'authUser', 'loggedInUser'];
      
      for (const key of possibleKeys) {
        const userStr = localStorage.getItem(key) || sessionStorage.getItem(key);
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user && (user.firstName || user.name || user.username)) {
            return {
              firstName: user.firstName || user.name?.split(' ')[0] || user.username || 'User',
              lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || ''
            };
          }
        }
      }
    } catch (error) {
      console.warn('Could not retrieve user info:', error);
    }
    
    // Fallback to a generic user if not available
    return {
      firstName: 'System',
      lastName: 'User'
    };
  }

  /**
   * Helper method to download blob
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    console.log(`Downloaded: ${filename}`);
  }

  /**
   * Get simple report templates
   */
  async getSimpleReportTemplates(): Promise<any[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<any[]>>(`${this.simpleReportsApiUrl}/simple-templates`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load simple report templates');
    }
    
    return response.data;
  }

  /**
   * Save a simple report configuration
   */
  async saveSimpleReport(reportData: any): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<any>>(`${this.simpleReportsApiUrl}/save`, reportData)
      );
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to save report');
      }
      
      return response.data;
    } catch (error: any) {
      // Extract error message from HTTP error response
      if (error.error) {
        const errorResponse = error.error;
        if (errorResponse.message) {
          throw new Error(errorResponse.message);
        }
        if (typeof errorResponse === 'string') {
          try {
            const parsed = JSON.parse(errorResponse);
            throw new Error(parsed.message || 'Failed to save report');
          } catch {
            throw new Error(errorResponse || 'Failed to save report');
          }
        }
      }
      // Fallback to error message or default
      throw new Error(error.message || 'Failed to save report');
    }
  }

  /**
   * Get saved simple reports
   */
  async getSavedSimpleReports(): Promise<any[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<any[]>>(`${this.simpleReportsApiUrl}/saved`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load saved reports');
    }
    
    return response.data;
  }

  /**
   * Get a specific saved simple report by ID
   */
  async getSavedSimpleReportById(reportId: string): Promise<any> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<any>>(`${this.simpleReportsApiUrl}/saved/${reportId}`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to load report');
    }
    
    return response.data;
  }

  /**
   * Update a saved simple report
   */
  async updateSavedSimpleReport(reportId: string, reportData: any): Promise<any> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<any>>(`${this.simpleReportsApiUrl}/saved/${reportId}`, reportData)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update report');
    }
    
    return response.data;
  }

  /**
   * Delete a saved simple report
   */
  async deleteSavedSimpleReport(reportId: string): Promise<any> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<any>>(`${this.simpleReportsApiUrl}/saved/${reportId}`)
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete report');
    }
    
    return response.data;
  }
}
